import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row, message } from 'antd';
import Button from 'components/generalUI/Button';
import { DayActivityTimeAvailability } from 'apollo/types';
import { TimedActivity } from 'views/TicketInformation/ticketInformation.viewModel';
import styles from './ActivityTimeSelector.module.scss';

interface ActivityTimeSelectorProps {
  activity: TimedActivity;
  ticketCount: number;
  selectedTimeId: string | undefined;
  onSelect: (activityId: string, activityTimeId: string) => void;
}

const ActivityTimeSelector: FC<ActivityTimeSelectorProps> = ({
  activity,
  ticketCount,
  selectedTimeId,
  onSelect,
}: ActivityTimeSelectorProps) => {
  const { t } = useTranslation();

  const hasNoRoom = (activityTime: DayActivityTimeAvailability) =>
    activityTime.remaining <= 0 || ticketCount > activityTime.remaining;

  const isDisabled = (activityTime: DayActivityTimeAvailability) =>
    hasNoRoom(activityTime) || ticketCount === 0;

  const renderStatus = (activityTime: DayActivityTimeAvailability) =>
    hasNoRoom(activityTime) && ticketCount > 0
      ? t('pages.ticketInformation.noRemaining')
      : '';

  // Raising the party size can invalidate an already chosen slot.
  useEffect(() => {
    const selected = activity.activityTimes.find(
      (time) => time.activityTimeId === selectedTimeId
    );
    if (selected && hasNoRoom(selected)) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      message.error(t('pages.ticketInformation.noActivityTimesRemaining'));
      onSelect(activity.id, '');
    }
  }, [ticketCount]);

  return (
    <div className={styles.Container}>
      <p className={styles.Subtitle}>
        {t('pages.ticketInformation.availableTimesDetail', {
          activity: activity.name,
        })}
      </p>
      <Row gutter={[8, 8]}>
        {activity.activityTimes.map((activityTime) => (
          <Col span={8} key={activityTime.activityTimeId}>
            <Button
              className={styles.TimeButton}
              kind={
                selectedTimeId === activityTime.activityTimeId ? 'primary' : 'secondary'
              }
              disabled={isDisabled(activityTime)}
              onClick={() => onSelect(activity.id, activityTime.activityTimeId)}
            >
              <span className={styles.TimeLabel}>
                {`${activityTime.startHour}-${activityTime.endHour}`}
                <span className={styles.Status}>{renderStatus(activityTime)}</span>
              </span>
            </Button>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default ActivityTimeSelector;
